/**
 * <smart-search> — AlphaPets header search panel (V3 全幅導購面板).
 *
 * Results come from the Section Rendering API rather than /search/suggest.json,
 * because that endpoint returns 417 "Unsupported buyer locale" on this shop.
 * Server-rendered cards mean prices, availability and translations can never
 * drift from the product page.
 *
 * Personalisation is layered on the client:
 *   1. Shopify returns results in its own relevance order.
 *   2. AlphaProfile (assets/browsing-profile.js) supplies an affinity profile
 *      built from what this shopper has actually looked at.
 *   3. We BOOST, never hide — affinity can reorder results but never removes
 *      one, and an out-of-stock product is never promoted to the top-hit slot.
 *
 * Recommendations come from Shopify's own ML via /recommendations/products.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 180;
  var MIN_QUERY = 1;
  /** How hard affinity is allowed to push against Shopify's relevance order. */
  var AFFINITY_BOOST = 0.45;
  /** Beyond this the three columns just get airy and the grid turns into a wall
   *  of small cards, so the panel stops growing and centres on the header. */
  var MAX_PANEL_WIDTH = 1560;

  /* ------------------------------------------------------------- helpers -- */

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments, ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function fill(tpl, vars) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) {
      return vars[k] == null ? '' : vars[k];
    });
  }

  function icon(path, size) {
    return '<svg width="' + (size || 15) + '" height="' + (size || 15) + '" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + path + '</svg>';
  }
  var ICON_SEARCH = icon('<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>');
  var ICON_CLOCK = icon('<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>');
  var ICON_TREND = icon('<path d="M4 17 9 11l4 4 7-8"/>');

  /* ------------------------------------------------------------- element -- */

  class SmartSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      try {
        this.config = JSON.parse(this.querySelector('[data-ss-config]').textContent);
      } catch (e) {
        return; // no config, no panel — leave the plain form working
      }
      this.S = this.config.strings || {};

      this.panel = this.querySelector('[data-ss-panel]');
      this.navEl = this.querySelector('[data-ss-nav]');
      this.mainEl = this.querySelector('[data-ss-main]');
      this.ctaEl = this.querySelector('[data-ss-viewall]');
      this.statusEl = this.querySelector('[data-ss-status]');
      this.overlay = document.querySelector('[data-ss-overlay]');
      // The snippet renders next to the header's search field, which puts the
      // backdrop inside .header-hydrogen's z-index: 10 stacking context — its
      // own z-index would then be scoped to layer 10 and page content would
      // paint over it. Re-home it on <body> alongside the panel.
      if (this.overlay && this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay);
      }

      this.cache = new Map();
      this.state = { query: '', items: [], filters: {}, browse: null, activeInput: null, seq: 0 };

      // The host stays hidden; .ss-panel is relocated into a visible host on focus.
      this.bindInputs();
      this.bindPanel();
    }

    /* ------------------------------------------------------------ binding */

    bindInputs() {
      var self = this;
      this.inputs = [];

      (this.config.inputs || []).forEach(function (cfg) {
        Array.prototype.forEach.call(document.querySelectorAll(cfg.selector), function (input) {
          var host = cfg.host ? document.querySelector(cfg.host) : null;
          if (!host) return; // host missing on this template — leave the form alone
          self.inputs.push({ input: input, host: host, mode: cfg.mode || 'anchored' });

          input.setAttribute('autocomplete', 'off');
          input.setAttribute('role', 'combobox');
          input.setAttribute('aria-expanded', 'false');
          input.setAttribute('aria-autocomplete', 'list');

          var run = debounce(function () { self.onQuery(input.value); }, DEBOUNCE_MS);

          input.addEventListener('focus', function () { self.activate(input); });
          input.addEventListener('input', function () { self.activate(input); run(); });
          input.addEventListener('keydown', function (e) { self.onKeydown(e, input); });
          // Focus alone is not enough: after Escape (or an outside click) the
          // field is still focused, so a second click would fire no focus event
          // and the panel would appear dead.
          input.addEventListener('click', function () { self.activate(input); });

          var form = input.form || input.closest('form');
          if (form) {
            form.addEventListener('submit', function () {
              self.remember(input.value);
            });
          }
        });
      });

      document.addEventListener('click', function (e) {
        if (self.panel.contains(e.target)) return;
        if (self.inputs.some(function (i) { return i.input === e.target || i.input.contains(e.target); })) return;
        // Clicks inside the variant picker are not "outside" clicks — otherwise
        // dismissing the picker would also throw away the shopper's results,
        // which is exactly what stepping the panel behind it is meant to avoid.
        if (e.target.closest && e.target.closest('quick-add-drawer-fun')) return;
        self.close();
      });

      // The + on each card opens the theme's own variant picker
      // (quick-add-drawer-fun, z-index 9999). Our panel sits at 99999, so the
      // picker would open BEHIND it. Step the panel down while the drawer is
      // open rather than closing it — the shopper keeps their results and can
      // carry on searching after adding.
      var qa = document.querySelector('quick-add-drawer-fun');
      if (qa && 'MutationObserver' in window) {
        new MutationObserver(function () {
          var open = qa.classList.contains('is-open');
          self.panel.classList.toggle('ss-behind', open);
          if (self.overlay) self.overlay.classList.toggle('ss-behind', open);
        }).observe(qa, { attributes: true, attributeFilter: ['class'] });
      }

      // The anchored panel is position:fixed against the header, so it has to
      // be re-measured whenever the header can move or change size.
      var reflow = function () {
        self.positionPanel();
        self.sizeInlineHost();
        self.guardFooterFromOverlays();
      };
      window.addEventListener('resize', reflow);
      window.addEventListener('orientationchange', reflow);
      // Mobile browser chrome and the on-screen keyboard change the usable
      // height without firing a window resize on every platform.
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', reflow);
        window.visualViewport.addEventListener('scroll', reflow);
      }
    }

    bindPanel() {
      var self = this;
      if (this.overlay) this.overlay.addEventListener('click', function () { self.close(); });

      this.ctaEl.addEventListener('click', function () {
        // When browsing a collection in-panel, "view all" must go to that
        // collection, not to a search for its name.
        if (self.state.browse) { window.location.href = self.state.browse.url; return; }
        var q = self.state.query;
        self.remember(q);
        window.location.href = q
          ? self.config.searchUrl + '?q=' + encodeURIComponent(q) + '&type=product'
          : '/collections/all';
      });

      this.panel.addEventListener('click', function (e) {
        // Every branch below re-renders, which DETACHES the clicked node. The
        // document-level outside-click listener then sees a target that is no
        // longer inside the panel and closes it — so pills, suggestions and
        // browse links all silently dismissed the panel they were driving.
        // Anything we handle here is by definition not an outside click.
        var handled = e.target.closest(
          '[data-ss-term],[data-ss-browse],[data-ss-facet],[data-ss-reset],[data-ss-clear-recent]'
        );
        if (handled) e.stopPropagation();

        var term = e.target.closest('[data-ss-term]');
        if (term) {
          e.preventDefault();
          var input = self.state.activeInput || (self.inputs[0] && self.inputs[0].input);
          if (input) { input.value = term.dataset.ssTerm; input.focus(); }
          self.onQuery(term.dataset.ssTerm);
          return;
        }

        var browse = e.target.closest('[data-ss-browse]');
        if (browse) {
          e.preventDefault();
          self.browse(browse.getAttribute('href'), browse.dataset.ssBrowse);
          return;
        }

        var facet = e.target.closest('[data-ss-facet]');
        if (facet) { e.preventDefault(); self.toggleFacet(facet.dataset.ssFacet, facet.dataset.ssValue); return; }

        if (e.target.closest('[data-ss-reset]')) { e.preventDefault(); self.state.filters = {}; self.paint(); return; }
        if (e.target.closest('[data-ss-clear-recent]')) {
          e.preventDefault();
          if (window.AlphaProfile) window.AlphaProfile.reset();
          self.renderZero();
          return;
        }

        var link = e.target.closest('a[href]');
        if (link) self.remember(self.state.query);
      });
    }

    /* ------------------------------------------------------------ opening */

    activate(input) {
      var entry = this.inputs.filter(function (i) { return i.input === input; })[0];
      if (!entry) return;

      // `.header-hydrogen` is z-index: 10 (assets/header-glassmorphism.css) and
      // creates a stacking context, so a panel nested inside it paints BELOW the
      // account dropdown, quick-add drawer, chat widget and even our own dim.
      // For the anchored (desktop) panel we therefore host it on <body> and
      // position it against the header manually.
      var target = entry.mode === 'anchored' ? document.body : entry.host;
      if (this.panel.parentElement !== target) target.appendChild(this.panel);

      // Inline hosts get a marker class so their own layout can adapt to
      // suddenly containing a tall panel. The mobile search card in particular
      // is bottom-anchored and grows UPWARD, which pushed its input off-screen.
      if (this.inlineHost && this.inlineHost !== entry.host) this.releaseInlineHost(this.inlineHost);
      this.inlineHost = entry.mode === 'inline' ? entry.host : null;
      if (this.inlineHost) this.inlineHost.classList.add('ss-has-panel');

      this.panel.dataset.mode = entry.mode;
      this.state.activeInput = input;
      this.state.anchorHost = entry.mode === 'anchored' ? entry.host : null;
      // Keep state.query in step with the field we are opening against.
      this.state.query = input.value.trim();

      var wasClosed = this.panel.hidden;
      this.panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');

      if (entry.mode === 'anchored') {
        this.positionPanel();
        this.lockScroll();
        if (this.overlay) {
          this.overlay.hidden = false;
          requestAnimationFrame(function (o) { return function () { o.classList.add('is-visible'); }; }(this.overlay));
        }
      } else {
        // Inline hosts (mobile overlay / mega drawer) supply their own backdrop.
        this.panel.style.top = this.panel.style.left = this.panel.style.width = '';
        this.unlockScroll();
        this.sizeInlineHost();
        if (this.overlay) {
          this.overlay.classList.remove('is-visible');
          this.overlay.hidden = true;
        }
      }

      if (wasClosed && !input.value.trim()) this.renderZero();
      else if (wasClosed) this.onQuery(input.value);
    }

    /**
     * Pin the anchored panel to the bottom edge of the header.
     *
     * Sized against BOTH axes: short viewports (landscape phones, split screen)
     * get a tighter gap and margin so the vertical budget goes to results, and
     * ultra-wide viewports get the panel capped and centred on the header
     * rather than stretched into a 2000px band.
     */
    positionPanel() {
      var host = this.state.anchorHost;
      if (!host || this.panel.hidden || this.panel.dataset.mode !== 'anchored') return;

      var r = host.getBoundingClientRect();
      // visualViewport tracks mobile browser chrome and the on-screen keyboard.
      var vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      var vw = window.innerWidth;

      var short = vh <= 520;
      var gap = short ? 4 : 10;
      var margin = vw > 860 ? 12 : 6;
      var bottomBreathing = short ? 6 : 16;

      var top = Math.max(0, r.bottom + gap);
      var available = Math.max(0, vw - margin * 2);
      var width = Math.min(available, Math.max(0, r.width - margin * 2), MAX_PANEL_WIDTH);
      var left = Math.round(r.left + (r.width - width) / 2);
      left = Math.min(Math.max(margin, left), Math.max(margin, vw - width - margin));

      this.panel.style.top = top + 'px';
      this.panel.style.left = left + 'px';
      this.panel.style.width = width + 'px';
      this.panel.style.setProperty('--ss-max-h', Math.max(180, vh - top - bottomBreathing) + 'px');

      // The dim starts below the header so the search field stays visible and
      // clickable rather than being covered by its own backdrop.
      if (this.overlay) this.overlay.style.top = Math.max(0, r.bottom) + 'px';
    }

    /**
     * Size the mobile sheet to the space the keyboard actually leaves.
     *
     * The theme's card is `position: absolute; bottom: 85px`, measured against
     * the LAYOUT viewport — which does not shrink when the on-screen keyboard
     * opens. So the card kept claiming full height, ran on behind the keyboard,
     * and the input (then on the bottom row) was stranded in the dead band
     * above it. With a 注音 keyboard that band is taller still, because the
     * candidate strip sits above the keys.
     *
     * `visualViewport` is the only thing that reports this correctly: its
     * height excludes the keyboard AND its accessory/candidate bars, and its
     * offsetTop reports how far the browser scrolled the page to reveal the
     * focused field. Take both, and the sheet ends exactly where the keyboard
     * begins, with the entry box pinned at the top where nothing can cover it.
     */
    sizeInlineHost() {
      var host = this.inlineHost;
      if (!host || this.panel.hidden || this.panel.dataset.mode !== 'inline') return;

      var vv = window.visualViewport;
      var vh = (vv && vv.height) || window.innerHeight;
      var offsetTop = (vv && vv.offsetTop) || 0;

      // A shrunken visual viewport means the keyboard is up. The theme's bottom
      // nav is behind it at that point, so its 85px reservation is dead space.
      var keyboardUp = window.innerHeight - vh > 150;
      var bottomGap = keyboardUp ? 8 : 85;
      var avail = Math.max(0, vh - bottomGap);
      // Landscape with the keyboard up leaves ~180px. Claw back the top gap
      // rather than spend it on whitespace.
      var top = avail < 280 ? 4 : 12;

      // No minimum height: a floor here (it was 220) makes the sheet overrun
      // the keyboard on a landscape phone, which is the one thing this method
      // exists to prevent. A cramped sheet still scrolls; a covered one does not.
      host.style.top = (offsetTop + top) + 'px';
      host.style.bottom = 'auto';
      host.style.height = Math.max(0, avail - top) + 'px';
    }

    /** Hand the mobile sheet back to the theme's own CSS. */
    releaseInlineHost(host) {
      if (!host) return;
      host.classList.remove('ss-has-panel');
      host.style.top = host.style.bottom = host.style.height = '';
    }

    /**
     * Keep the footer CTA clear of third-party floating widgets.
     *
     * The storefront's chat bubble is fixed bottom-right at z-index 2147483000
     * — nothing sane can outrank it, and hiding a merchant's support widget is
     * not ours to do. So instead of guessing its coordinates (an app can move
     * or remove it), probe inward from the footer's trailing edge and reserve
     * however much is actually covered. Resolves to 0 when nothing is there.
     */
    guardFooterFromOverlays() {
      var foot = this.panel.querySelector('.ss-panel__foot');
      if (!foot || this.panel.hidden) return;

      this.panel.style.setProperty('--ss-safe-end', '0px');
      var r = foot.getBoundingClientRect();
      if (r.width < 80 || r.height < 10) return;

      var y = Math.round(r.top + r.height / 2);
      var edge = Math.round(r.right) - 3;
      var limit = Math.round(r.left) + 60; // never eat more than most of the bar
      var safe = 0;

      for (var x = edge; x > limit; x -= 8) {
        var el = document.elementFromPoint(x, y);
        if (!el || this.panel.contains(el)) break;
        safe = edge - x + 12;
      }
      this.panel.style.setProperty('--ss-safe-end', safe + 'px');
    }

    lockScroll() {
      if (this.scrollLocked) return;
      this.scrollLocked = true;
      var sbw = window.innerWidth - document.documentElement.clientWidth;
      this.prevOverflow = document.documentElement.style.overflow;
      this.prevPadding = document.body.style.paddingInlineEnd;
      document.documentElement.style.overflow = 'hidden';
      if (sbw > 0) document.body.style.paddingInlineEnd = sbw + 'px';
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      this.scrollLocked = false;
      document.documentElement.style.overflow = this.prevOverflow || '';
      document.body.style.paddingInlineEnd = this.prevPadding || '';
    }

    close() {
      if (this.panel.hidden) return;
      this.panel.hidden = true;
      this.unlockScroll();
      if (this.inlineHost) { this.releaseInlineHost(this.inlineHost); this.inlineHost = null; }
      if (this.overlay) { this.overlay.classList.remove('is-visible'); this.overlay.hidden = true; }
      this.inputs.forEach(function (i) { i.input.setAttribute('aria-expanded', 'false'); });
      this.state.activeInput = null;
      this.state.anchorHost = null;
    }

    remember(q) {
      q = String(q || '').trim();
      if (q && window.AlphaProfile) window.AlphaProfile.trackSearch(q);
    }

    announce(msg) { if (this.statusEl) this.statusEl.textContent = msg; }

    /* ----------------------------------------------------------- fetching */

    async fetchSection(url) {
      if (this.cache.has(url)) return this.cache.get(url);
      var res = await fetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var payload = doc.querySelector('[data-ss-payload]');
      var out = {
        count: payload ? Number(payload.dataset.count || 0) : 0,
        performed: payload ? payload.dataset.performed === 'true' : false,
        intent: payload ? payload.dataset.intent || '' : '',
        heading: payload ? payload.dataset.heading || '' : '',
        cards: payload ? Array.prototype.slice.call(payload.querySelectorAll('[data-ss-item]')) : []
      };
      this.cache.set(url, out);
      return out;
    }

    resultsUrl(q) {
      return this.config.searchUrl + '?q=' + encodeURIComponent(q) +
        '&type=product&options[prefix]=last&section_id=' + this.config.panelSection;
    }

    recsUrl(productId, intent, limit) {
      return '/recommendations/products?product_id=' + productId +
        '&limit=' + (limit || 4) + '&intent=' + intent +
        '&section_id=' + this.config.recsSection;
    }

    /* ------------------------------------------------------------ queries */

    async onQuery(raw) {
      var q = String(raw || '').trim();
      this.state.query = q;
      this.state.filters = {};
      this.state.browse = null;

      if (q.length < MIN_QUERY) { this.renderZero(); return; }

      var seq = ++this.state.seq;
      this.showSkeleton();
      this.announce(this.S.loading);

      try {
        var data = await this.fetchSection(this.resultsUrl(q));
        if (seq !== this.state.seq) return; // a newer keystroke won

        this.state.items = this.rerank(this.applyCoverage(data.cards.map(readCard), q));
        this.paint();
        this.announce(this.state.weak
          ? fill(this.S.weakMatch, { query: q }) + '，' + this.S.weakMatchHint
          : fill(this.S.resultsFor, { query: q, count: this.state.items.length }));
      } catch (e) {
        if (seq !== this.state.seq) return;
        this.renderError();
      }
    }

    /**
     * Render a collection (brand, category, or a header menu link) inside the
     * panel instead of navigating away. The results section answers collection
     * URLs too, so the whole pipeline — ranking, facets, quick add — is reused.
     */
    async browse(url, label) {
      if (!url || url === '#') return;
      var seq = ++this.state.seq;
      this.showSkeleton();
      this.announce(this.S.loading);

      try {
        var sep = url.indexOf('?') > -1 ? '&' : '?';
        var data = await this.fetchSection(url + sep + 'section_id=' + this.config.panelSection);
        if (seq !== this.state.seq) return;

        this.state.browse = { label: data.heading || label || '', url: url };
        this.state.filters = {};
        this.state.weak = false; // a collection is browsed, not matched
        this.state.items = this.rerank(data.cards.map(readCard));
        this.paint();
        this.announce(fill(this.S.resultsFor, {
          query: this.state.browse.label, count: this.state.items.length
        }));
      } catch (e) {
        if (seq === this.state.seq) this.renderError();
      }
    }

    /**
     * Re-order by how much of the query a product actually contains, and
     * decide whether the result set deserves an "approximate" caveat.
     *
     * Availability stays the primary key: Shopify already returns sold-out
     * last and shoppers should keep seeing buyable stock first, so coverage
     * only sorts *within* the in-stock and sold-out groups. Nothing is
     * removed — a low-coverage product is demoted, never hidden.
     */
    applyCoverage(items, q) {
      var terms = queryTerms(q);
      this.state.weak = false;
      if (!terms.length || items.length < 2) return items;

      var best = 0;
      var scored = items.map(function (it, i) {
        var c = coverage(it, terms);
        if (c > best) best = c;
        return { it: it, c: c, i: i };
      });

      // Nothing matched the specific half of the query. Keep Shopify's order
      // (sorting noise by noise helps nobody) and let paintMain say so.
      if (!best) { this.state.weak = true; return items; }
      if (best < 0.5) this.state.weak = true;

      scored.sort(function (a, b) {
        return (b.it.available - a.it.available) || (b.c - a.c) || (a.i - b.i);
      });
      return scored.map(function (x) { return x.it; });
    }

    /**
     * Blend Shopify's relevance order with the shopper's affinity profile.
     * Boost only: nothing is ever removed, and out-of-stock never takes #1.
     */
    rerank(items) {
      if (this.config.personalise === false) return items;
      if (!window.AlphaProfile || items.length < 2) return items;
      var aff = window.AlphaProfile.affinity();
      if (!aff.confidence) return items;

      var n = items.length;
      var scored = items.map(function (it, i) {
        var relevance = (n - i) / n;
        var boost = window.AlphaProfile.score(it, aff);
        return { it: it, s: relevance + boost * AFFINITY_BOOST, i: i, boost: boost };
      });
      scored.sort(function (a, b) { return b.s - a.s || a.i - b.i; });

      var out = scored.map(function (x) {
        x.it.personalised = x.boost > 0.12;
        x.it.reason = x.it.reason || (x.boost > 0.12 ? window.AlphaProfile.reason(x.it, aff) : '');
        return x.it;
      });

      if (out.length && !out[0].available) {
        var k = -1;
        for (var j = 0; j < out.length; j++) { if (out[j].available) { k = j; break; } }
        if (k > 0) out.unshift(out.splice(k, 1)[0]);
      }
      return out;
    }

    facets(items) {
      // A facet count must equal what the shopper gets after clicking it.
      // `cat`/`dog` therefore include species-neutral products, because
      // filtered() keeps those too — counting only strict matches made the
      // pills lie (貓 claimed 3, showed 6). `catOnly`/`dogOnly` are the strict
      // counts, used solely to decide whether a cat/dog split is meaningful.
      var types = {}, vendors = {}, inStock = 0, onSale = 0;
      var species = { cat: 0, dog: 0, catOnly: 0, dogOnly: 0 };
      items.forEach(function (p) {
        if (p.type) types[p.type] = (types[p.type] || 0) + 1;
        if (p.vendor) vendors[p.vendor] = (vendors[p.vendor] || 0) + 1;
        if (p.species === 'cat') { species.cat++; species.catOnly++; }
        else if (p.species === 'dog') { species.dog++; species.dogOnly++; }
        else { species.cat++; species.dog++; }
        if (p.available) inStock++;
        if (p.discount > 0) onSale++;
      });
      var rank = function (o) {
        return Object.keys(o).map(function (k) { return { key: k, count: o[k] }; })
          .sort(function (a, b) { return b.count - a.count; });
      };
      return { types: rank(types), vendors: rank(vendors), species: species, inStock: inStock, onSale: onSale };
    }

    filtered() {
      var f = this.state.filters;
      return this.state.items.filter(function (p) {
        if (f.species && p.species !== f.species && p.species !== 'both') return false;
        if (f.type && p.type !== f.type) return false;
        if (f.vendor && p.vendor !== f.vendor) return false;
        if (f.inStock && !p.available) return false;
        if (f.onSale && !p.discount) return false;
        return true;
      });
    }

    toggleFacet(key, value) {
      var f = this.state.filters;
      if (key === 'inStock' || key === 'onSale') {
        if (f[key]) delete f[key]; else f[key] = true;
      } else if (f[key] === value) {
        delete f[key];
      } else {
        f[key] = value;
      }
      this.paint();
    }

    /* ---------------------------------------------------------- rendering */

    showSkeleton() {
      this.navEl.innerHTML = '';
      this.mainEl.innerHTML = '<div class="ss-skeleton">' +
        '<span></span><span></span><span></span><span></span><span></span><span></span></div>';
      this.ctaEl.textContent = this.S.browseAll;
    }

    renderError() {
      this.navEl.innerHTML = '';
      this.mainEl.innerHTML = '<div class="ss-empty"><b>' + esc(this.S.error) + '</b></div>';
      this.ctaEl.textContent = this.S.browseAll;
    }

    paint() {
      var q = this.state.query;
      var items = this.filtered();

      this.paintNav(q, items);
      this.paintMain(q, items);

      this.ctaEl.textContent = fill(this.S.viewAll, { count: items.length });
      this.guardFooterFromOverlays();
    }

    paintNav(q, items) {
      var f = this.facets(items);
      var html = '';

      var sugg = [];
      if (f.types[0] && q.indexOf(f.types[0].key) === -1) sugg.push({ label: q + ' ' + f.types[0].key, n: f.types[0].count });
      if (f.vendors[0]) {
        var short = f.vendors[0].key.split(' ')[0];
        if (q.indexOf(short) === -1) sugg.push({ label: q + ' ' + short, n: f.vendors[0].count });
      }
      if (sugg.length) {
        html += '<div class="ss-h">' + esc(this.S.suggestions) + '</div>';
        sugg.forEach(function (s) {
          html += '<button type="button" class="ss-sug" data-ss-term="' + esc(s.label) + '">' +
            ICON_SEARCH + '<span>' + esc(s.label) + '</span><span class="ss-sug__c">' + s.n + '</span></button>';
        });
      }

      if (f.vendors.length) {
        html += '<div class="ss-h">' + esc(this.S.brands) + '</div>';
        f.vendors.slice(0, 5).forEach(function (v) {
          var url = (items.filter(function (i) { return i.vendor === v.key; })[0] || {}).vendorUrl || '#';
          html += '<a class="ss-navitem" href="' + esc(url) + '" data-ss-browse="' + esc(v.key) + '">' +
            '<span class="ss-navitem__ico">🏷️</span><span class="ss-navitem__t">' + esc(v.key) + '</span>' +
            '<span class="ss-navitem__c">' + v.count + '</span></a>';
        });
      }

      if (f.types.length) {
        html += '<div class="ss-h">' + esc(this.S.categories) + '</div>';
        f.types.slice(0, 6).forEach(function (t) {
          var sample = items.filter(function (i) { return i.type === t.key; })[0] || {};
          var glyph = sample.species === 'cat' ? '🐱' : sample.species === 'dog' ? '🐶' : '🏠';
          html += '<a class="ss-navitem" href="' + esc(sample.typeUrl || '#') + '" data-ss-browse="' + esc(t.key) + '">' +
            '<span class="ss-navitem__ico">' + glyph + '</span><span class="ss-navitem__t">' + esc(t.key) + '</span>' +
            '<span class="ss-navitem__c">' + t.count + '</span></a>';
        });
      }

      this.navEl.innerHTML = html;
    }

    paintMain(q, items) {
      var self = this;
      var f = this.facets(this.state.items);
      var filters = this.state.filters;

      this.mainEl.innerHTML = '';

      var count = document.createElement('div');
      count.className = 'ss-count';
      var label = this.state.browse ? this.state.browse.label : q;
      count.innerHTML = fill(this.S.resultsFor, { query: esc(label), count: '<b>' + items.length + '</b>' })
        .replace('「' + esc(label) + '」', '「<b>' + esc(label) + '</b>」');
      this.mainEl.appendChild(count);

      // Say so when none of the results actually contain what was asked for.
      // Without this the shopper cannot tell "we stock this" from "we stock
      // nothing like this" — both render as a full, confident grid.
      if (this.state.weak && !this.state.browse && items.length) {
        var warn = document.createElement('div');
        warn.className = 'ss-approx';
        warn.innerHTML = '<b>' + esc(fill(this.S.weakMatch, { query: label })) + '</b>' +
          '<span>' + esc(this.S.weakMatchHint) + '</span>';
        this.mainEl.appendChild(warn);
      }

      var pills = document.createElement('div');
      pills.className = 'ss-pills';
      var pill = function (key, value, label, n) {
        var on = key === 'inStock' || key === 'onSale' ? !!filters[key] : filters[key] === value;
        return '<button type="button" class="ss-pill" data-ss-facet="' + key + '" data-ss-value="' + esc(value) + '" ' +
          'aria-pressed="' + on + '">' + esc(label) + '<small>' + n + '</small></button>';
      };
      var ph = '';
      // Only offer the split when the results genuinely contain both a cat-only
      // and a dog-only product — otherwise every result matches both pills and
      // neither one narrows anything.
      if (f.species.catOnly && f.species.dogOnly) {
        ph += pill('species', 'cat', this.S.cat, f.species.cat) + pill('species', 'dog', this.S.dog, f.species.dog);
      }
      f.types.slice(0, 5).forEach(function (t) { ph += pill('type', t.key, t.key, t.count); });
      if (f.inStock && f.inStock < this.state.items.length) ph += pill('inStock', '1', this.S.inStock, f.inStock);
      if (f.onSale) ph += pill('onSale', '1', this.S.onSale, f.onSale);
      if (Object.keys(filters).length) {
        ph += '<button type="button" class="ss-pill" data-ss-reset>' + esc(this.S.reset) + ' ✕</button>';
      }
      pills.innerHTML = ph;
      this.mainEl.appendChild(pills);

      if (!items.length) {
        var empty = document.createElement('div');
        empty.className = 'ss-empty';
        empty.innerHTML = '<b>' + esc(fill(this.S.noResults, { query: q })) + '</b>' + esc(this.S.noResultsHint);
        this.mainEl.appendChild(empty);
        return;
      }

      var grid = document.createElement('div');
      grid.className = 'ss-grid';
      // Every result renders here now. The top hit used to be pulled out into
      // the merch column and skipped in this loop; with that column gone it
      // would have silently vanished from the results.
      items.forEach(function (item) {
        grid.appendChild(item.el);
      });
      this.mainEl.appendChild(grid);
      self.gridItems = Array.prototype.slice.call(grid.querySelectorAll('.ss-card'));
    }

    /** Order the merchandiser's trending terms by this shopper's species lean. */
    personalisedTrending() {
      var list = (this.config.trending || []).map(function (t) { return String(t).trim(); }).filter(Boolean);
      if (!window.AlphaProfile) return list;
      var lead = window.AlphaProfile.affinity().leadSpecies;
      if (!lead) return list;
      var match = lead === 'cat' ? /貓|喵/ : /狗|犬|汪/;
      var hit = [], rest = [];
      list.forEach(function (t) { (match.test(t) ? hit : rest).push(t); });
      return hit.concat(rest);
    }

    /* ---------------------------------------------------------- zero state */

    async renderZero() {
      var self = this;
      // Same sequence guard onQuery() uses. Guarding on state.query instead was
      // fragile: it stays set after a search, so reopening the panel with an
      // empty field left the centre column stuck on its loading skeleton.
      var seq = ++this.state.seq;
      var P = window.AlphaProfile;
      var recent = P ? P.recent() : [];
      var searches = P ? P.searches() : [];

      /* --- left: recent + trending searches --- */
      var nav = '';
      if (searches.length) {
        nav += '<div class="ss-h">' + esc(this.S.recentSearches) +
          '<button type="button" class="ss-h__act" data-ss-clear-recent>' + esc(this.S.clear) + '</button></div>';
        searches.forEach(function (t) {
          nav += '<button type="button" class="ss-sug" data-ss-term="' + esc(t) + '">' + ICON_CLOCK + '<span>' + esc(t) + '</span></button>';
        });
      }
      var trending = this.personalisedTrending();
      if (trending.length) {
        nav += '<div class="ss-h">' + esc(this.S.trending) + '</div>';
        trending.forEach(function (t) {
          nav += '<button type="button" class="ss-sug" data-ss-term="' + esc(t) + '">' + ICON_TREND + '<span>' + esc(t) + '</span></button>';
        });
      }
      this.navEl.innerHTML = nav;

      this.ctaEl.textContent = this.S.browseAll;
      this.guardFooterFromOverlays();

      /* --- centre: continue browsing + Shopify ML picks --- */
      this.mainEl.innerHTML = '<div class="ss-skeleton"><span></span><span></span><span></span><span></span></div>';

      var blocks = [];
      if (recent.length) {
        var ids = recent.map(function (r) { return r.id; });
        try {
          var q = ids.map(function (id) { return 'id:' + id; }).join(' OR ');
          var data = await this.fetchSection(this.resultsUrl(q));
          if (data.cards.length) {
            // Shopify returns these in relevance order; restore recency order.
            var byId = {};
            data.cards.forEach(function (c) { byId[c.dataset.id] = c; });
            var ordered = ids.map(function (id) { return byId[id]; }).filter(Boolean);
            blocks.push({ title: this.S.recent, cards: ordered.slice(0, 6) });
          }
        } catch (e) { /* fall through to recommendations only */ }

        try {
          var recs = await this.fetchSection(this.recsUrl(recent[0].id, 'related', 6));
          if (recs.cards.length) blocks.push({ title: this.S.forYou, cards: recs.cards.slice(0, 6) });
        } catch (e) { /* optional */ }
      }

      if (seq === this.state.seq && !this.panel.hidden) {
        if (!blocks.length) {
          this.mainEl.innerHTML = '';
          var pills = (this.config.quickLinks || []).map(function (l) {
            return '<a class="ss-pill" href="' + esc(l.url) + '" data-ss-browse="' + esc(l.title) + '">' + esc(l.title) + '</a>';
          }).join('');
          if (pills) {
            this.mainEl.innerHTML = '<div class="ss-h">' + esc(self.S.alsoBrowse) + '</div><div class="ss-pills">' + pills + '</div>';
          }
        } else {
          this.mainEl.innerHTML = '';
          blocks.forEach(function (b) {
            var h = document.createElement('div');
            h.className = 'ss-h';
            h.textContent = b.title;
            self.mainEl.appendChild(h);
            var grid = document.createElement('div');
            grid.className = 'ss-grid';
            b.cards.forEach(function (c) { grid.appendChild(c.cloneNode(true)); });
            self.mainEl.appendChild(grid);
          });
        }
        this.gridItems = Array.prototype.slice.call(this.mainEl.querySelectorAll('.ss-card'));
      }
    }

    /* ------------------------------------------------------------ keyboard */

    onKeydown(e, input) {
      if (e.key === 'Escape') { this.close(); input.blur(); return; }
      if (e.key === 'Enter') { this.remember(input.value); return; }

      var items = this.gridItems || [];
      if (!items.length || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
      e.preventDefault();

      var current = items.indexOf(this.activeCard);
      current += e.key === 'ArrowDown' ? 1 : -1;
      if (current < 0) current = items.length - 1;
      if (current >= items.length) current = 0;

      items.forEach(function (el) { el.classList.remove('is-active'); });
      this.activeCard = items[current];
      this.activeCard.classList.add('is-active');
      this.activeCard.scrollIntoView({ block: 'nearest' });
      var link = this.activeCard.querySelector('.ss-card__link');
      if (link) input.setAttribute('aria-activedescendant', link.href);
    }
  }

  /**
   * Split a query into the units we can actually check a product against.
   *
   * Chinese has no word delimiters, and Shopify's search ORs whatever it
   * tokenises, so a two-concept query collapses onto its most common
   * character: 「幼貓奶粉」 matches every product containing 貓 and returns
   * cat litter first, because 賓士牌貓砂 repeats 貓砂 across title, type and
   * tags. The store carries no 奶粉 at all — 「奶粉」 alone returns zero — yet
   * the shopper is shown a confident 20-result grid.
   *
   * Bigrams are the right unit: Traditional Chinese product nouns are
   * overwhelmingly two characters (貓砂, 潔牙, 凍乾, 主食), so 幼貓奶粉 yields
   * 幼貓 / 貓奶 / 奶粉 and a litter bag matches none of them. Latin runs are
   * kept whole, since those genuinely are space-delimited.
   */
  function queryTerms(q) {
    var terms = [];
    var cjk = q.match(/[㐀-鿿豈-﫿]+/g) || [];
    cjk.forEach(function (run) {
      if (run.length === 1) { terms.push(run); return; }
      for (var i = 0; i < run.length - 1; i++) terms.push(run.slice(i, i + 2));
    });
    (q.match(/[a-z0-9]+/gi) || []).forEach(function (w) {
      if (w.length > 1) terms.push(w.toLowerCase());
    });
    return terms;
  }

  /** Fraction of the query's terms that appear in a product's own text. */
  function coverage(item, terms) {
    if (!terms.length) return 1;
    var hay = ((item.title || '') + ' ' + (item.type || '') + ' ' + (item.vendor || '')).toLowerCase();
    var hits = 0;
    for (var i = 0; i < terms.length; i++) if (hay.indexOf(terms[i]) > -1) hits++;
    return hits / terms.length;
  }

  /** Turn a server-rendered card element into the object the ranker needs. */
  function readCard(el) {
    var d = el.dataset;
    return {
      el: el,
      id: Number(d.id),
      url: d.url,
      title: d.title,
      vendor: d.vendor,
      vendorUrl: d.vendorUrl,
      type: d.type,
      typeUrl: d.typeUrl,
      species: d.species,
      price: parseFloat(d.price),
      available: d.available === 'true',
      discount: Number(d.discount) || 0,
      img: d.img,
      priceHtml: d.priceHtml || '',
      reason: d.reason || ''
    };
  }

  if (!customElements.get('smart-search')) {
    customElements.define('smart-search', SmartSearch);
  }
})();
